import React, { useState, useEffect } from 'react';
import { ArrowLeft, Zap, TrendingUp, Clock, Calendar, CreditCard, History } from 'lucide-react';
import API_BASE_URL from '../config/api';

const API_URL = API_BASE_URL;

function CreditsPage({ onBack }) {
  const [credits, setCredits] = useState({
    balance: 15,
    totalEarned: 15,
    totalSpent: 0,
    nextRechargeDate: null,
    lastRechargeDate: null
  });
  const [loading, setLoading] = useState(true);
  const [usageHistory, setUsageHistory] = useState([]);

  useEffect(() => {
    fetchCredits();
    fetchUsageHistory();
  }, []);

  const fetchCredits = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/credits?user_id=anonymous`);
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setCredits({
            balance: result.data.balance || 15,
            totalEarned: result.data.totalEarned || 15,
            totalSpent: result.data.totalSpent || 0,
            nextRechargeDate: result.data.nextRechargeDate ? new Date(result.data.nextRechargeDate) : null,
            lastRechargeDate: result.data.lastRechargeDate ? new Date(result.data.lastRechargeDate) : null
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch credits:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/detective/history?user_id=anonymous&limit=20`);
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // Transform history to show point usage
          const history = result.data.analyses?.map((analysis, index) => ({
            id: analysis._id || `usage-${index}`,
            type: 'Photo Location Search',
            pointsUsed: 5,
            date: new Date(analysis.created_at),
            address: analysis.address?.formatted || 'Unknown location'
          })) || [];
          setUsageHistory(history);
        }
      }
    } catch (error) {
      console.error('Failed to fetch usage history:', error);
    }
  };

  const handleUpgrade = () => {
    // Open upgrade modal or navigate to billing
    alert('Upgrade feature coming soon!\n\nPremium features:\n✓ Unlimited analyses\n✓ Advanced location insights\n✓ Priority support\n✓ Export reports');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Dashboard</span>
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 font-heading">Credits & Payment</h1>
              <p className="text-slate-600">Manage your Propr Points and usage</p>
            </div>
          </div>
        </div>

        {/* 2-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Points Overview - Takes 2 columns */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-xl font-bold text-slate-900 font-heading mb-6">Points Overview</h2>

            {loading ? (
              <div className="text-center py-8">
                <div className="text-slate-500">Loading...</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Credits Remaining */}
                <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg p-6 text-white">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-5 h-5" />
                    <span className="text-sm font-medium text-primary-100">Credits Remaining</span>
                  </div>
                  <div className="text-4xl font-bold">{credits.balance}</div>
                  <div className="text-xs text-primary-100 mt-1">5 points per analysis</div>
                </div>

                {/* Analysis Performed */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-slate-600" />
                    <span className="text-sm font-medium text-slate-600">Analyses Performed</span>
                  </div>
                  <div className="text-4xl font-bold text-slate-900">{Math.floor(credits.totalSpent / 5)}</div>
                  <div className="text-xs text-slate-500 mt-1">All time total</div>
                </div>

                {/* Points Spent */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <History className="w-5 h-5 text-slate-600" />
                    <span className="text-sm font-medium text-slate-600">Points Spent</span>
                  </div>
                  <div className="text-4xl font-bold text-slate-900">{credits.totalSpent}</div>
                  <div className="text-xs text-slate-500 mt-1">Lifetime usage</div>
                </div>
              </div>
            )}
          </div>

          {/* Upgrade CTA - Takes 1 column */}
          <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-lg p-6 text-white flex flex-col justify-between">
            <div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4">
                <CreditCard className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold font-heading mb-2">Running Low on Points?</h3>
              <p className="text-primary-100 text-sm mb-6">
                Upgrade to Premium for unlimited analyses and advanced features
              </p>
            </div>
            <button
              onClick={handleUpgrade}
              className="w-full bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-all hover:scale-105 shadow-lg"
            >
              Upgrade Plan
            </button>
          </div>
        </div>

        {/* Recharge Schedule & Usage History */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recharge Schedule */}
          {credits.nextRechargeDate && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-xl font-bold text-slate-900 font-heading mb-6">Recharge Schedule</h2>

              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-primary-600" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-600 mb-1">Next Recharge</div>
                  <div className="text-lg font-bold text-slate-900">
                    {credits.nextRechargeDate.toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {Math.ceil((credits.nextRechargeDate - new Date()) / (1000 * 60 * 60 * 24))} days remaining
                  </div>
                </div>
              </div>

              {credits.lastRechargeDate && (
                <div className="flex items-start gap-4 p-4 bg-green-50 rounded-xl mt-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Clock className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-green-900 mb-1">Last Recharge</div>
                    <div className="text-lg font-bold text-green-900">
                      {credits.lastRechargeDate.toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                    <div className="text-xs text-green-600 mt-1">
                      +15 points added
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Usage History */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900 font-heading">Usage History</h2>
              <span className="text-sm text-slate-500">{usageHistory.length} analyses</span>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {usageHistory.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm font-medium">No usage history yet</p>
                  <p className="text-slate-400 text-xs mt-1">Start analyzing properties to see your usage here</p>
                </div>
              ) : (
                usageHistory.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="p-2 bg-primary-100 rounded-lg">
                      <Zap className="w-4 h-4 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900">{item.type}</div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">{item.address}</div>
                      <div className="text-xs text-slate-400 mt-1">
                        {item.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-slate-900">
                      -{item.pointsUsed}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div >
  );
}

export default CreditsPage;
